import React, { useEffect, useRef } from 'react';
import type { QuickMessageContent, QuickMessageResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

interface QuickMessagePickerProps {
  /** Filtered quick messages to show. */
  items: QuickMessageResponse[];
  /** Currently highlighted index (driven by ChatInput keyboard nav). */
  selectedIndex: number;
  /** Select a quick message (Enter / click). */
  onSelect: (qm: QuickMessageResponse) => void;
  /** Sync the highlight when hovering with the mouse. */
  onHover: (index: number) => void;
}

const textOf = (content: QuickMessageContent[]): string => {
  const textPart = content.find((c) => c.type === 'text');
  return textPart && textPart.type === 'text' ? textPart.text : '';
};

const mediaIcon = (content: QuickMessageContent[]): { name: IconName; label: string } | null => {
  const media = content.find((c) => c.type !== 'text');
  if (!media) return null;
  switch (media.type) {
    case 'image':
      return { name: 'image', label: 'Imagem' };
    case 'video':
      return { name: 'video', label: 'Vídeo' };
    case 'audio':
      return { name: 'music', label: 'Áudio' };
    default:
      return { name: 'file', label: 'Arquivo' };
  }
};

/**
 * Floating quick-message picker rendered ABOVE the chat composer. Triggered by
 * "/" in ChatInput; keyboard navigation (↑↓ Enter Esc) is handled by ChatInput.
 * Light theme fixed (dropdown rule).
 */
const QuickMessagePicker: React.FC<QuickMessagePickerProps> = ({
  items,
  selectedIndex,
  onSelect,
  onHover,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the highlighted item visible as the user arrows through.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  return (
    <div
      className="absolute left-2 right-2 bottom-full mb-2 z-50 rounded-xl overflow-hidden"
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
      }}
      data-testid="quick-message-picker"
    >
      <div
        className="px-3 py-2 flex items-center gap-1.5"
        style={{ borderBottom: '1px solid #f1f5f9' }}
      >
        <Icon name="zap" className="w-3.5 h-3.5" style={{ color: '#8b5cf6' }} />
        <span className="text-xs font-semibold" style={{ color: '#64748b' }}>
          Mensagens rápidas
        </span>
        <span className="text-xs ml-auto" style={{ color: '#94a3b8' }}>
          ↑↓ navegar · Enter selecionar · Esc fechar
        </span>
      </div>

      <div ref={listRef} className="max-h-[280px] overflow-y-auto py-1">
        {items.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm" style={{ color: '#94a3b8' }}>
            Nenhuma mensagem encontrada
          </div>
        ) : (
          items.map((qm, index) => {
            const isSelected = index === selectedIndex;
            const snippet = textOf(qm.content);
            const media = mediaIcon(qm.content);
            return (
              <button
                key={qm.id}
                type="button"
                data-index={index}
                data-testid={`quick-message-picker-item-${qm.id}`}
                onMouseEnter={() => onHover(index)}
                onMouseDown={(e) => {
                  // Prevent the textarea from losing focus before selection.
                  e.preventDefault();
                  onSelect(qm);
                }}
                className="w-full text-left px-3 py-2 flex items-start gap-2.5"
                style={{ backgroundColor: isSelected ? '#f5f3ff' : 'transparent' }}
              >
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {qm.shortcut && (
                        <span
                          className="text-xs font-mono font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                          style={{ backgroundColor: '#ede9fe', color: '#7c3aed' }}
                        >
                          /{qm.shortcut}
                        </span>
                      )}
                      <span className="text-sm font-medium truncate" style={{ color: '#1e293b' }}>
                        {qm.title}
                      </span>
                      {qm.scope === 'company' && (
                        <span
                          className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
                          style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}
                        >
                          Empresa
                        </span>
                      )}
                    </div>
                    {(snippet || media) && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {media && (
                          <span
                            className="inline-flex items-center gap-1 text-xs flex-shrink-0"
                            style={{ color: '#8b5cf6' }}
                          >
                            <Icon name={media.name} className="w-3 h-3" />
                            {!snippet && media.label}
                          </span>
                        )}
                        {snippet && (
                          <span className="text-xs truncate" style={{ color: '#94a3b8' }}>
                            {snippet}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

QuickMessagePicker.displayName = 'QuickMessagePicker';

export default QuickMessagePicker;
